import { Link } from "react-router-dom";
import { contactInfo, footerMenuLinks, footerServices, popularSearches, socialLinks } from "@/mocks/site";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-accent-950 text-background-100">
      {/* Popular search — horizontal strip above the widgets block */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-screen-2xl px-4 py-8 md:px-6 md:py-10">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-primary-300">
            Popular Search
          </h3>
          <ul className="mt-4 flex flex-wrap gap-2.5">
            {popularSearches.map((p) => (
              <li key={p.label}>
                <Link
                  to={p.href}
                  className="inline-block whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-background-200 transition-colors hover:border-primary-400/40 hover:bg-primary-500/10 hover:text-primary-200"
                >
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mx-auto max-w-screen-2xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex w-full items-center rounded-lg bg-background-50 px-3 py-2.5">
              <img
                src={contactInfo.logo}
                alt="Lahari Packers and Movers Bangalore logo"
                title="Lahari Packers and Movers Bangalore"
                className="h-auto w-full object-contain"
              />
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-background-200">
              <strong className="font-semibold text-background-100">#1 Moving Service, Bangalore</strong>
              <br />
              A Bangalore based moving company offering house shifting, office relocation, local shifting, domestic and
              corporate moves, plus car &amp; bike transportation and warehouse storage. As trusted packers and movers in
              Bangalore, we handle packing, wrapping, loading and unloading at the best price.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="group flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-background-100 transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={`${s.icon} text-base transition-transform duration-300 ease-out group-hover:scale-110`} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading text-base font-semibold text-background-50">Our Services</h3>
            <ul className="mt-5 space-y-3">
              {footerServices.map((s) => (
                <li key={s.label}>
                  <Link
                    to={s.href}
                    className="flex items-center gap-2 text-sm text-background-200 transition-colors hover:text-primary-300"
                  >
                    <i className="ri-arrow-right-s-line text-primary-400" />
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading text-base font-semibold text-background-50">Contact Address</h3>
            <ul className="mt-5 space-y-4 text-sm text-background-200">
              <li className="flex gap-3">
                <i className="ri-map-pin-2-line mt-0.5 text-lg text-primary-400" />
                <span>{contactInfo.address}</span>
              </li>
              <li className="flex gap-3">
                <i className="ri-time-line mt-0.5 text-lg text-primary-400" />
                <span>
                  <strong className="font-semibold text-background-100">Mon to Sat:</strong> 9:30 AM – 6:30 PM
                  <br />
                  <strong className="font-semibold text-background-100">Sunday:</strong> 10:30 AM – 2:30 PM
                  <br />
                  <strong className="font-semibold text-background-100">Support:</strong> 24 × 7 Customer Support
                </span>
              </li>
              <li className="flex gap-3">
                <i className="ri-phone-line mt-0.5 text-lg text-primary-400" />
                <span>
                  <a href={contactInfo.phoneHref} className="hover:text-primary-300">{contactInfo.phone}</a>
                  <br />
                  <a href={contactInfo.phoneAltHref} className="hover:text-primary-300">{contactInfo.phoneAlt}</a>
                </span>
              </li>
              <li className="flex gap-3">
                <i className="ri-mail-line mt-0.5 text-lg text-primary-400" />
                <a href={contactInfo.emailHref} className="break-all hover:text-primary-300">
                  {contactInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-heading text-base font-semibold text-background-50">Quick Links</h3>
            <ul className="mt-5 space-y-3">
              {footerMenuLinks.map((m) => (
                <li key={m.label}>
                  <Link
                    to={m.href}
                    className="flex items-center gap-2 text-sm text-background-200 transition-colors hover:text-primary-300"
                  >
                    <i className="ri-arrow-right-s-line text-primary-400" />
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-screen-2xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-background-300 md:flex-row md:px-6">
          <p>
            © {year} Lahari Packers and Movers, Bangalore. All Rights Reserved. Powered by:{" "}
            <a
              href="https://www.digicube.net.in"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary-300 transition-colors hover:text-primary-200"
            >
              Digicube Solutions
            </a>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5">
            <Link to="/terms" className="hover:text-primary-300">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-primary-300">Privacy Policy</Link>
            <Link to="/gallery" className="hover:text-primary-300">Gallery</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}